/**
 * The offer that opens after a kit is added to the cart, ported from the
 * Claude Design canvas ("Setbox Home", the `upsellOpen` overlay, 2026-09-16).
 *
 * The buy box is a plain form posting to /cart/add, which is what makes the
 * purchase work without JavaScript. A full page post, though, navigates away —
 * so for the dialog to exist at all the add has to be sent by fetch instead.
 * That is all this module changes: intercept the submit, add the kit, open the
 * dialog. If it never loads, the form posts as before and the visitor lands on
 * the cart, which is the behaviour without JavaScript too.
 *
 * Horizon's own cart icon listens for CartLinesUpdateEvent, so the event is
 * dispatched here rather than reimplementing the bubble — that is the reason
 * this file is a module.
 */
import { CartLinesUpdateEvent } from '@shopify/events';
// Horizon's own formatter, so the note quotes the total the way the rest of
// the store writes money — the `money` filter's format, not Intl's guess.
import { formatMoney } from '@theme/money-formatting';

class SetboxUpsell {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('[data-setbox-buy-form]');
    this.dialog = root.parentElement?.querySelector('[data-setbox-upsell]');
    this.note = root.querySelector('[data-buy-note]');
    this.noteText = root.querySelector('[data-buy-note-text]');
    this.addUrl = root.dataset.cartAddUrl || '/cart/add';
    this.cartUrl = root.dataset.cartUrl || '/cart';
    this.currency = root.dataset.currency || 'USD';
    this.format = root.dataset.moneyFormat || '${{amount}}';

    if (!this.form || !this.dialog) return;

    this.addOn = this.dialog.querySelector('[data-setbox-upsell-add]');

    this.form.addEventListener('submit', this.onSubmit);
    this.addOn?.addEventListener('click', this.onAddOn);
    this.dialog
      .querySelector('[data-setbox-upsell-decline]')
      ?.addEventListener('click', this.onDecline);

    // Every way out of this dialog ends at the cart, so the cart is opened
    // from `close` rather than from each of the three controls. `close` does
    // not bubble, but this listener is on the dialog itself, so it does not
    // need to. It also catches Escape and the backdrop, which is the point:
    // by the time this dialog exists the kit is already in the cart, and
    // dismissing an offer is not a reason to hide that.
    this.dialog.addEventListener('close', this.onClose);
  }

  onSubmit = (event) => {
    if (typeof this.dialog.showModal !== 'function') return; // let it post
    event.preventDefault();

    this.reset();

    const submit = this.form.querySelector('[type="submit"]');
    if (submit) submit.disabled = true;

    this.add(new FormData(this.form))
      .then(() => this.dialog.showModal())
      .catch(() => {
        // Nothing was added, so the honest fallback is the post the visitor
        // asked for in the first place.
        this.form.removeEventListener('submit', this.onSubmit);
        this.form.submit();
      })
      .finally(() => {
        if (submit) submit.disabled = false;
      });
  };

  onAddOn = (event) => {
    const button = this.addOn ?? event.currentTarget;
    const id = button.dataset.setboxUpsellAdd;
    if (!id) return;

    button.disabled = true;
    const body = new FormData();
    body.append('id', id);
    body.append('quantity', '1');

    this.add(body)
      .then((cart) => {
        this.showNote(cart);
        // Closing opens the cart — see the `close` listener in the constructor.
        this.dialog.close();
      })
      .catch(() => {
        // Leave the dialog open: the visitor can try again or decline, which
        // is better than closing on them with nothing said.
        button.disabled = false;
      });
  };

  /**
   * Adds lines and tells the rest of the theme, then answers with the cart so
   * the note can quote a real total.
   */
  async add(body) {
    // `sections` is deliberately not requested: nothing on this page renders
    // cart contents, so there is nothing to morph back in.
    const deferred = CartLinesUpdateEvent.createPromise();
    this.root.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId: String(body.get('id')),
            quantity: Number(body.get('quantity')) || 1,
          },
        ],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(this.addUrl + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const added = await response.json();
      // /cart/add.js answers 422 with a `description` rather than throwing.
      if (!response.ok || added.status) throw new Error(added.description || 'add failed');

      const cart = await fetch(this.cartUrl + '.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, source: 'setbox-upsell' },
      });
      return cart;
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  }

  /**
   * Puts the offer back to how it started.
   *
   * The dialog and the note outlive a single purchase: nothing here reloads
   * the page, so a visitor can buy, empty the cart from the drawer and buy
   * again, and they meet the same DOM every time. Two things carried over and
   * both were wrong on the second pass — the add button stayed disabled from
   * the first successful add onwards, so the adapter looked unavailable, and
   * the note under the buy box went on quoting a cart total that no longer
   * existed.
   *
   * Done at the start of a purchase rather than at the end of the last one,
   * because the end has several exits and a purchase has one entrance.
   */
  reset() {
    if (this.addOn) this.addOn.disabled = false;
    if (this.note) this.note.hidden = true;
  }

  onDecline = (event) => {
    // The href is the no-JavaScript path. With the script running the visitor
    // stays where they are and the cart comes to them.
    event.preventDefault();
    this.dialog.close();
  };

  onClose = () => {
    const drawer = document.getElementById('cart-drawer');

    // Deferred a frame: this fires while the browser is still unwinding the
    // dialog that just closed, and the drawer's panel is a dialog too.
    // Opening one from inside the other's `close` puts two showModal() calls
    // in the same tick, and the top of the stack is then whichever the
    // browser settled on, not whichever we meant.
    requestAnimationFrame(() => {
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      // No drawer on this page — the cart page is the honest equivalent.
      window.location.href = this.cartUrl;
    });
  };

  showNote(cart) {
    if (!this.note || !this.noteText) return;
    const template = this.dialog.dataset.note || '';
    if (!template) return;

    this.noteText.textContent = template.replace(
      '[total]',
      formatMoney(cart.total_price, this.format, this.currency)
    );
    this.note.hidden = false;
  }
}

document.querySelectorAll('setbox-buy-box').forEach((root) => new SetboxUpsell(root));
